/**
 * Git-artiger Versionslog für ein Content-Block-Feld (C1 + Variant-D-Veredelung).
 *
 * Pro Version: Avatar (User/KI/System) · AKTUELL/vN · Zeitstempel · Autor ·
 * Kind-Pill · Notiz · Wert-Vorschau · Aktionen Vorschau / Wiederherstellen /
 * Vergleichen. „Vorschau" blendet die Version IM Feld ein (onPreview).
 */

import { useTranslation } from 'react-i18next'
import { Eye, RotateCcw, GitCompareArrows } from 'lucide-react'
import { Loading } from '@/components/v2/ui'
import { useVersionHistory } from '@/hooks/useVersionHistory'
import { EID } from '@/lib/element-ids.registry'
import { readLangText } from './contentBlockLang'
import { badgeFor, avatarFor, type ActivityBadge } from './activityFeed'
import type { ContentBlockVersion } from '@/types/versioning'

interface Props {
  blockId: string
  lang: string
  currentVersionId: string | null | undefined
  previewVersionId: string | null
  onPreview: (version: ContentBlockVersion) => void
  onRestore: (text: string) => void
  onCompare: () => void
}

const KIND_CLS: Record<ActivityBadge, string> = {
  edited: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
  ai: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  sync: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
  created: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
}

export function FieldVersionDropdown({ blockId, lang, currentVersionId, previewVersionId, onPreview, onRestore, onCompare }: Props) {
  const { t, i18n } = useTranslation()
  const { data, isLoading } = useVersionHistory({ entityType: 'content_block', entityId: blockId })
  const versions = [...(data?.versions ?? [])].sort((a, b) => b.version_number - a.version_number)

  return (
    <div
      className="field-version-dropdown mt-2 rounded-md border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/60"
      role="region"
      aria-label={t('productContent.channels.versions.title')}
    >
      {isLoading && <div className="flex justify-center py-3"><Loading size="sm" /></div>}
      {!isLoading && versions.length === 0 && (
        <p className="px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400">{t('productContent.channels.versions.empty')}</p>
      )}
      <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
        {versions.map((v) => {
          const badge = badgeFor(v)
          const av = avatarFor(badge)
          const isCurrent = v.id === currentVersionId
          const isPreview = v.id === previewVersionId
          const text = readLangText(v.content, lang)
          return (
            <li key={v.id} className={`field-version-item flex gap-2.5 px-3 py-2.5 ${isPreview ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}>
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${av.cls}`} aria-hidden="true">{av.letter}</span>
              <div className="min-w-0 flex-1 text-sm">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="font-mono font-bold px-1.5 py-0.5 rounded bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200">
                    {isCurrent ? t('productContent.channels.versions.current') : `v${v.version_number}`}
                  </span>
                  <span className="font-mono font-semibold text-neutral-700 dark:text-neutral-200">{new Date(v.created_at).toLocaleString(i18n.language)}</span>
                  <span className="text-neutral-400">{v.changed_by_name ?? 'System'}</span>
                  <span className={`font-mono px-1.5 py-0.5 rounded ${KIND_CLS[badge]}`}>{t(`productContent.channels.activity.badge.${badge}`)}</span>
                </div>
                {v.diff_summary && <p className="text-xs italic text-neutral-500 dark:text-neutral-400 mt-1">{v.diff_summary}</p>}
                <p className="text-xs text-neutral-700 dark:text-neutral-300 mt-1 line-clamp-2 rounded bg-white dark:bg-neutral-950 px-2 py-1">{text || t('content.noContent')}</p>
                <div className="flex items-center gap-3 mt-1.5 text-xs">
                  {!isCurrent && (
                    <>
                      <button type="button" id={`${EID['content-version-preview-btn']}-${v.id}`} className="field-version-preview flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline" onClick={() => onPreview(v)} aria-pressed={isPreview}>
                        <Eye className="h-3 w-3" />{isPreview ? t('productContent.channels.versions.previewActive') : t('productContent.channels.versions.preview')}
                      </button>
                      <button type="button" id={`${EID['content-version-restore-btn']}-${v.id}`} className="field-version-restore flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline" onClick={() => onRestore(text)}>
                        <RotateCcw className="h-3 w-3" />{t('productContent.channels.versions.restore')}
                      </button>
                    </>
                  )}
                  <button type="button" id={`${EID['content-version-compare-btn']}-${v.id}`} className="field-version-compare flex items-center gap-1 text-neutral-500 dark:text-neutral-300 hover:underline" onClick={onCompare}>
                    <GitCompareArrows className="h-3 w-3" />{t('productContent.channels.versions.compare')}
                  </button>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
